<template>
    <div class="modal-wrapper">
        <div class="modal fade show" style="display: block" tabindex="-1" @click.self="$emit('close')">
            <div class="modal-dialog modal-lg">
                <div :class="['modal-content', { dark: theme === THEMES.DARK }]">
                    <div class="modal-header">
                        <h5 class="modal-title">
                            <i class="material-icons mr-2">inventory</i>
                            Products in Sale {{ sale?.name }}
                        </h5>
                        <button type="button" class="close" @click="$emit('close')">
                            <span>&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <div v-if="loading" class="text-center py-4">
                            <div class="spinner-border" role="status">
                                <span class="sr-only">Loading...</span>
                            </div>
                            <p class="mt-2">Loading products...</p>
                        </div>

                        <div v-else-if="products.length > 0">
                            <!-- Sale Summary -->
                            <div class="sale-summary mb-4 p-3 border rounded">
                                <div class="row">
                                    <div class="col-md-6">
                                        <strong>Customer:</strong> {{ sale.customer_name || 'Guest Customer' }}<br>
                                        <strong>Location:</strong> {{ sale.set_warehouse }}<br>
                                        <strong>Sale Date:</strong> {{ sale.formatted_date }}
                                    </div>
                                    <div class="col-md-6 text-right">
                                        <strong>Total Items:</strong> {{ products.length }}<br>
                                        <strong>Subtotal:</strong> ${{ formatCurrency(sale.net_total) }}<br>
                                        <strong>Grand Total:</strong> <span class="text-success">${{ formatCurrency(sale.grand_total) }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Products Table -->
                            <CustomTable style="font-size: 13px" bordered>
                                <template #head>
                                    <tr>
                                        <th>#</th>
                                        <th>Product Name</th>
                                        <th>SKU</th>
                                        <th>Quantity</th>
                                        <th>Unit Price</th>
                                        <th>Discount</th>
                                        <th>Total</th>
                                    </tr>
                                </template>
                                <template #body>
                                    <tr v-for="(product, index) in products" :key="product.id || index">
                                        <td>{{ index + 1 }}</td>
                                        <td>
                                            <div class="product-info">
                                                <strong>{{ product.item_name || product.name }}</strong>
                                                <br>
                                                <small class="text-muted">{{ product.description }}</small>
                                            </div>
                                        </td>
                                        <td>
                                            <code>{{ product.item_code }}</code>
                                        </td>
                                        <td>
                                            <span class="badge badge-primary">{{ product.qty || product.quantity }}</span>
                                            <small class="text-muted d-block">{{ product.uom || 'ea' }}</small>
                                        </td>
                                        <td>
                                            ${{ formatCurrency(product.rate || product.unit_price) }}
                                        </td>
                                        <td>
                                            <span v-if="product.discount_amount > 0" class="text-warning">
                                                -${{ formatCurrency(product.discount_amount) }}
                                            </span>
                                            <span v-else class="text-muted">-</span>
                                        </td>
                                        <td>
                                            <strong class="text-success">
                                                ${{ formatCurrency(product.amount || (product.qty * product.rate)) }}
                                            </strong>
                                        </td>
                                    </tr>
                                </template>
                            </CustomTable>

                            <!-- Tax and Totals Summary -->
                            <div class="totals-summary mt-4 p-3 bg-light rounded" v-if="sale.taxes && sale.taxes.length > 0">
                                <h6>Tax Breakdown:</h6>
                                <div v-for="tax in sale.taxes" :key="tax.id" class="d-flex justify-content-between">
                                    <span>{{ tax.account_head }} ({{ tax.rate }}%):</span>
                                    <span>${{ formatCurrency(tax.tax_amount) }}</span>
                                </div>
                            </div>
                        </div>

                        <div v-else-if="!loading" class="text-center py-4">
                            <i class="material-icons text-muted" style="font-size: 48px">shopping_basket</i>
                            <h5 class="text-muted mt-2">No products found</h5>
                            <p class="text-muted">Unable to load products for this sale.</p>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <Button variant="secondary" borderOnly @click="viewFullInvoice">
                            <i class="material-icons mr-1">receipt</i>
                            View Full Invoice
                        </Button>
                        <Button variant="secondary" @click="$emit('close')">
                            Close
                        </Button>
                    </div>
                </div>
            </div>
        </div>
        <div class="modal-backdrop fade show"></div>
    </div>
</template>

<script>
import { http } from "@/utils/http"
import { mapState } from "vuex"
import CustomTable from "../layout/CustomTable.vue"
import Button from "@/components/UI/Button.vue"

export default {
    name: "SaleProductsModal",
    props: {
        sale: {
            type: Object,
            required: true
        }
    },
    components: {
        CustomTable,
        Button,
    },
    data() {
        return {
            loading: true,
            products: [],
            error: null
        }
    },
    computed: {
        ...mapState("misc", ["theme", "THEMES"])
    },
    async mounted() {
        await this.fetchProducts()
    },
    methods: {
        async fetchProducts() {
            try {
                this.loading = true
                // Fetch detailed invoice data including products
                const res = await http.get(`/invoices/${this.sale.name}`)
                
                if (res.data && res.data.items) {
                    this.products = res.data.items
                } else if (res.data && res.data.result && res.data.result.items) {
                    this.products = res.data.result.items
                } else {
                    // Fallback: try to get from invoice items endpoint
                    const itemsRes = await http.get(`/invoice-items?invoice_id=${this.sale.id || this.sale.name}`)
                    this.products = itemsRes.data.result || []
                }
            } catch (err) {
                console.error("Error fetching invoice products:", err)
                this.error = "Failed to load products"
                this.$toast.error("Error loading products for this sale")
            }
            this.loading = false
        },

        formatCurrency(amount) {
            if (!amount) return "0.00"
            return parseFloat(amount).toFixed(2)
        },

        viewFullInvoice() {
            this.$emit('close')
            this.$router.push(`/invoices/${this.sale.name}`)
        }
    }
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";
.modal-content {
    &.dark {
        background-color: $darkCardBg;
        color: $darkTextColor;
        border-color: $darkBorderColor;
        
        .modal-header {
            border-bottom-color: $darkBorderColor;
        }
        
        .modal-footer {
            border-top-color: $darkBorderColor;
        }
        
        .close {
            color: white;
            opacity: 0.8;
            
            &:hover {
                opacity: 1;
            }
        }
    }
}

.sale-summary {
    background: $lightHoverColor;
    
    .modal-content.dark & {
        background: lighten($darkCardBg, 6%);
        border-color: $darkBorderColor;
    }
}

.product-info {
    min-width: 200px;
}

.totals-summary {
    .modal-content.dark & {
        background: lighten($darkCardBg, 6%) !important;
        color: $darkTextColor;
    }
}

.modal-backdrop {
    background-color: rgba(0, 0, 0, 0.5);
}

.badge {
    font-size: 0.85em;
}

code {
    font-size: 0.9em;
    background: $lightHoverColor;
    padding: 2px 4px;
    border-radius: 3px;
    
    .modal-content.dark & {
        background: lighten($darkCardBg, 10%);
        color: $darkTextColor;
    }
}
</style> 
